/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { apiClient } from './api';
import type { ApiResponse } from '../types/api';
import type { AssurancePortfolio, AssurancePortfolioStakeholder, Organization } from '../types/assurance';
import type { DownloadDocument } from '../types/document';
import type { SurveyModelMinData } from '../types/survey';
import type { SurveyActionData } from '../types/surveyAction';
import type { Suggestion } from '../types/search';
import type { Action } from '../constants/action';

interface SurveyAssurancePortfolios {
  survey: SurveyModelMinData;
  assurances: AssurancePortfolio[];
}

interface AssurancePortfolioQuestions {
  survey: SurveyActionData;
  current: string[];
}

interface DelegateQuestionsRequest {
  utrvAssuranceIds: string[];
  action: Action;
  userId?: string;
}

export function getSurveyAssurancePortfolios(surveyId: string) {
  return apiClient.get<ApiResponse<SurveyAssurancePortfolios>>('/assurances/portfolio/survey/' + surveyId)
    .then(response => response.data.data)
}

export function getSurveyAssurancePortfolio(assurancePortfolioId: string) {
  return apiClient.get<ApiResponse<AssurancePortfolio>>(`/assurances/portfolio/${assurancePortfolioId}`)
    .then(response => response.data.data)
}

export function getSurveyAssurancePortfolioQuestions(portfolioId: string) {
  return apiClient.get<ApiResponse<AssurancePortfolioQuestions>>(`/assurances/portfolio/${portfolioId}/questions`)
    .then(response => response.data.data)
}

export function downloadPortfolioBundle(portfolioId: string) {
  return apiClient.get<ApiResponse<DownloadDocument[]>>(`/assurances/portfolio/${portfolioId}/download`)
    .then(response => response.data.data)
}

export function downloadMultipleQuestions(portfolioId: string, data: { questionIds: string[] }) {
  return apiClient.post<ApiResponse<DownloadDocument[]>>(`/assurances/portfolio/${portfolioId}/download/multiple`, data)
    .then(response => response.data.data)
}

export function downloadQuestionBundle(portfolioId: string, questionId: string) {
  return apiClient.get<ApiResponse<DownloadDocument[]>>(`/assurances/portfolio/${portfolioId}/questions/${questionId}/download`)
    .then(response => response.data.data)
}

export function updateSurveyAssurancePortfolioQuestions(portfolioId: string, data: { questions: string[] }) {
  return apiClient.post<ApiResponse<unknown>>(`/assurances/portfolio/${portfolioId}/questions`, data)
    .then(response => response.data.data)
}

export const getAssurerList = () => apiClient
  .get<ApiResponse<Pick<Organization, '_id' | 'name'>[]>>('/assurances/assurers')
  .then((response) => response.data.data);

export function setAssurancePortfolioReady(portfolioId: string) {
  return apiClient.patch<ApiResponse<unknown>>(`/assurances/portfolio/${portfolioId}/ready`)
    .then(response => response.data.data);
}

export function getDelegatedAssurers(portfolioId: string, data: { utrvAssuranceIds: string[] }) {
  return apiClient.post<ApiResponse<AssurancePortfolioStakeholder[]>>(`/assurances/portfolio/${portfolioId}/questions/delegate`, data)
    .then(response => response.data.data)
}

export function delegateQuestions(portfolioId: string, data: DelegateQuestionsRequest) {
  return apiClient.patch<ApiResponse<AssurancePortfolioStakeholder>>(`/assurances/portfolio/${portfolioId}/questions/delegate`, data)
    .then(response => response.data.data)
}

export function searchUserByAssurancePortfolio(escapedString: string, portfolioId: string) {
  return apiClient.get<ApiResponse<Suggestion[]>>(`/assurances/portfolio/${portfolioId}/search?s=${escapedString}`)
    .then(response => response.data.data.map(d => {
      d.name = `${d.firstName} ${d.surname}`;
      return d;
    }));
}